import React, { useEffect } from 'react';
import { Drawer, Form, Select, Switch, Input, Button, message } from 'antd';
import * as actions from "store/actions/AuthActions";
import useIsMobile from 'hooks/useIsMobile';

const { TextArea } = Input;

const DealContactRoleForm = ({ visible, onClose, dealId, instance, contacts, onSuccess }) => {
    const [form] = Form.useForm();
    const isMobile = useIsMobile();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (visible && instance) {
            form.setFieldsValue({
                contact: instance.contact,
                role: instance.role,
                is_primary: instance.is_primary,
                notes: instance.notes,
            });
        } else if (visible && !instance) {
            form.resetFields();
        }
    }, [visible, instance]);

    const handleSubmit = async (values) => {
        try {
            const data = {
                ...values,
                deal: dealId,
            };

            if (instance) {
                await actions.updateDealContactRole(data, instance.id);
                message.success('Contact role updated successfully');
            } else {
                await actions.addDealContactRole(data);
                message.success('Contact role added successfully');
            }

            form.resetFields();
            onSuccess();
        } catch (error) {
            console.error('Contact Role Error:', error);
            console.error('Error Response:', error.response?.data);
            const errorMsg = error.response?.data?.detail || error.response?.data?.error || JSON.stringify(error.response?.data) || 'Failed to save contact role';
            message.error(errorMsg);
        }
    };

    return (
        <Drawer
            title={`${instance ? 'Edit' : 'Add'} Contact Role`}
            placement={isMobile ? "bottom" : "right"}
            width={isMobile ? "100%" : 500}
            height={isMobile ? "90vh" : undefined}
            onClose={onClose}
            open={visible}
            styles={{
                body: { paddingBottom: isMobile ? 80 : 24 }
            }}
            footer={
                <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: '8px' }}>
                    <Button onClick={onClose} style={isMobile ? { flex: 1 } : undefined}>Cancel</Button>
                    <Button type="primary" onClick={() => form.submit()} style={isMobile ? { flex: 1 } : undefined}>
                        {instance ? 'Update' : 'Add'}
                    </Button>
                </div>
            }
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    label="Contact"
                    name="contact"
                    rules={[{ required: true, message: 'Please select a contact!' }]}
                >
                    <Select
                        showSearch
                        placeholder="Select contact"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={contacts.map(contact => ({
                            value: contact.id,
                            label: `${contact.first_name} ${contact.last_name}`
                        }))}
                    />
                </Form.Item>

                <Form.Item
                    label="Role"
                    name="role"
                    rules={[{ required: true, message: 'Please select a role!' }]}
                >
                    <Select placeholder="Select role">
                        <Select.Option value="DEVELOPER_EVALUATOR">Developer / Evaluator</Select.Option>
                        <Select.Option value="DECISION_MAKER">Decision Maker</Select.Option>
                        <Select.Option value="PURCHASING">Purchasing</Select.Option>
                        <Select.Option value="EXECUTIVE_SPONSOR">Executive Sponsor</Select.Option>
                        <Select.Option value="ENGINEERING_LEAD">Engineering Lead</Select.Option>
                        <Select.Option value="ECONOMIC_DECISION_MAKER">Economic Decision Maker</Select.Option>
                        <Select.Option value="PRODUCT_MANAGEMENT">Product Management</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Primary Contact"
                    name="is_primary"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>

                <Form.Item
                    label="Notes"
                    name="notes"
                >
                    <TextArea rows={4} placeholder="Additional notes..." />
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default DealContactRoleForm;
